import React, { useState, useEffect, useCallback } from 'react';
import { creditorsApi, vehiclesApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Bank,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  X,
  Check,
  Warning,
  Money,
  Percent,
  Eye,
  CalendarBlank,
  Table as TableIcon,
  FilePdf,
  FileXls,
  DownloadSimple,
  Trash
} from '@phosphor-icons/react';

const statusColors = {
  activo: 'badge-success',
  liquidado: 'badge-neutral',
  reestructurado: 'badge-warning',
  vencido: 'badge-danger',
  cancelado: 'badge-neutral'
};

const statusLabels = {
  activo: 'Activo',
  liquidado: 'Liquidado',
  reestructurado: 'Reestructurado',
  vencido: 'Vencido',
  cancelado: 'Cancelado'
};

const Creditors = () => {
  const { canManage, canEdit, isAdmin } = useAuth();
  const [creditors, setCreditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showAmortModal, setShowAmortModal] = useState(false);
  const [showMonthlySummary, setShowMonthlySummary] = useState(false);
  const [monthlyData, setMonthlyData] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [amortData, setAmortData] = useState(null);
  const [amortLoading, setAmortLoading] = useState(false);
  const [creditorPayments, setCreditorPayments] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedCreditor, setSelectedCreditor] = useState(null);
  const [editingCreditor, setEditingCreditor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'PF',
    credit_type: 'simple',
    rate: '',
    loan_amount: '',
    start_date: '',
    term_months: 12,
    monthly_payment: '',
    vehicle_value: '',
    initial_payment: '',
    total_rents: '',
    purchase_option: '',
    vehicle_id: ''
  });
  const [vehicles, setVehicles] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [capitalPayment, setCapitalPayment] = useState('');
  const [interestPayment, setInterestPayment] = useState('');
  const [ivaInterest, setIvaInterest] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [persistingSnapshot, setPersistingSnapshot] = useState(false);
  const [snapshotMsg, setSnapshotMsg] = useState('');
  const [backfilling, setBackfilling] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [linkingExcel, setLinkingExcel] = useState(false);
  const excelFileRef = React.useRef(null);

  useEffect(() => {
    loadCreditors();
    vehiclesApi.getAll().then(({ data }) => setVehicles(data)).catch(() => setVehicles([]));
  }, []);

  const loadCreditors = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await creditorsApi.getAll();
      setCreditors(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePersistSnapshot = async () => {
    if (!window.confirm(
      'Esto guardará en la base de datos los valores que ves AHORA en la tabla ' +
      '(Saldo Actual, Pago Mensual, Total Rentas, Rentas por Pagar y Monto Préstamo) ' +
      'como la VERSIÓN FINAL. Los acreedores quedarán marcados como "congelados". ¿Continuar?'
    )) return;

    try {
      setPersistingSnapshot(true);
      setSnapshotMsg('');
      const items = creditors.map((c) => {
        const isLease = c.credit_type === 'arrendamiento';
        const rentasPend = Number(c.rentas_pendientes || 0);
        const saldo = Number(c.current_balance || 0);
        const deudaIndividual = isLease ? rentasPend : saldo;
        return {
          id: c.id,
          current_balance: saldo,
          monthly_payment: Number(c.monthly_payment || 0),
          loan_amount: Number(c.loan_amount || 0),
          total_rents: Number(c.total_rents || 0),
          rentas_pendientes: rentasPend,
          deuda_total: Number(deudaIndividual.toFixed(2)),
          initial_payment: Number(c.initial_payment || 0),
          rate: Number(c.rate || 0),
          vehicle_value: Number(c.vehicle_value || 0),
          purchase_option: Number(c.purchase_option || 0),
        };
      });
      const { data } = await creditorsApi.persistSnapshot(items);
      setSnapshotMsg(
        `Versión final guardada en BD (${data.items_updated}/${items.length} acreedores).`
      );
      await loadCreditors();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setPersistingSnapshot(false);
    }
  };

  const handleBulkLinkExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(
      `Se leerá "${file.name}" y se vincularán automáticamente los acreedores a sus vehículos según las columnas "Acreedor", "Pago Mensual" y "Placas". ¿Continuar?`
    )) {
      if (excelFileRef.current) excelFileRef.current.value = '';
      return;
    }
    try {
      setLinkingExcel(true);
      setSnapshotMsg('');
      setError('');
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await creditorsApi.bulkLinkVehiclesFromExcel(fd);
      const parts = [`✅ Vinculados ${data.linked} de ${data.total_rows_processed} filas.`];
      if (data.unmatched_vehicle?.length) parts.push(`⚠️ Placas no encontradas en vehículos: ${data.unmatched_vehicle.length}`);
      if (data.unmatched_creditor?.length) parts.push(`⚠️ Acreedores no encontrados: ${data.unmatched_creditor.length}`);
      setSnapshotMsg(parts.join(' · '));
      await loadCreditors();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLinkingExcel(false);
      if (excelFileRef.current) excelFileRef.current.value = '';
    }
  };

  const handleBackfillArrendamientos = async () => {
    if (!window.confirm(
      'Esto RECALCULA el desglose (Capital / Interés / IVA Renta / Saldo Después) ' +
      'y corrige el monto de TODOS los pagos mensuales ya registrados en acreedores tipo Arrendamiento ' +
      'usando la tabla de amortización francesa. No afecta pagos "Abono Capital". ¿Continuar?'
    )) return;
    try {
      setBackfilling(true);
      setSnapshotMsg('');
      const { data } = await creditorsApi.backfillArrendamientoComponents();
      setSnapshotMsg(
        `Backfill ejecutado: ${data.payments_updated} pagos actualizados en ${data.creditors_processed} acreedores.`
      );
      await loadCreditors();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBackfilling(false);
    }
  };

  const handleRegisterArrendamientosThrough = async () => {
    const defaultDate = '2026-07-31';
    const target = window.prompt(
      'Ingresa la fecha objetivo (YYYY-MM-DD). Se crearán como PAGADOS todos los pagos mensuales faltantes de arrendamiento hasta esa fecha.',
      defaultDate
    );
    if (!target) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(target)) {
      setError('Fecha inválida. Formato esperado: YYYY-MM-DD');
      return;
    }
    if (!window.confirm(
      `Se crearán pagos "Pagado" para TODOS los acreedores de Arrendamiento hasta ${target}. ¿Continuar?`
    )) return;
    try {
      setRegistering(true);
      setSnapshotMsg('');
      const { data } = await creditorsApi.registerArrendamientoThrough(target);
      setSnapshotMsg(
        `Registrados ${data.payments_created} pagos nuevos en ${data.creditors_processed} acreedores (objetivo ${target}).`
      );
      await loadCreditors();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setRegistering(false);
    }
  };

  const handleStatusChange = async (creditorId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'liquidado') {
        updateData.current_balance = 0;
      }
      await creditorsApi.update(creditorId, updateData);
      setCreditors(creditors.map(c => c.id === creditorId ? { ...c, status: newStatus, ...(newStatus === 'liquidado' ? { current_balance: 0 } : {}) } : c));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    const data = {
      ...formData,
      rate: parseFloat(formData.rate),
      loan_amount: parseFloat(formData.loan_amount || 0),
      term_months: parseInt(formData.term_months),
      monthly_payment: parseFloat(formData.monthly_payment),
    };
    if (formData.vehicle_id) {
      const v = vehicles.find((x) => x.id === formData.vehicle_id);
      data.vehicle_plate = v?.plates || '';
    } else {
      data.vehicle_id = null;
      data.vehicle_plate = null;
    }
    if (formData.credit_type === 'arrendamiento') {
      data.vehicle_value = parseFloat(formData.vehicle_value || 0);
      data.initial_payment = parseFloat(formData.initial_payment || 0);
      data.total_rents = parseFloat(formData.total_rents || 0);
      data.purchase_option = parseFloat(formData.purchase_option || 0);
    } else {
      // For non-leasing credits, strip leasing-only fields (avoid sending empty strings)
      delete data.vehicle_value;
      delete data.initial_payment;
      delete data.total_rents;
      delete data.purchase_option;
    }

    try {
      if (editingCreditor) {
        await creditorsApi.update(editingCreditor.id, data);
      } else {
        await creditorsApi.create(data);
      }
      setShowModal(false);
      setEditingCreditor(null);
      resetForm();
      loadCreditors();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      const isLease = selectedCreditor?.credit_type === 'arrendamiento';
      let payload;
      if (isLease) {
        const cap = parseFloat(capitalPayment || 0);
        const intr = parseFloat(interestPayment || 0);
        const iva = parseFloat(ivaInterest || 0);
        if (cap <= 0 && intr <= 0) {
          setFormError('Ingresa al menos el pago de Capital o Intereses.');
          setSaving(false);
          return;
        }
        payload = {
          capital_payment: cap,
          interest_payment: intr,
          iva_interest: iva,
          payment_date: paymentDate || undefined,
        };
      } else {
        payload = {
          amount: parseFloat(paymentAmount || 0),
          capital_payment: parseFloat(capitalPayment || 0),
          payment_date: paymentDate || undefined,
        };
      }
      await creditorsApi.recordPayment(selectedCreditor.id, payload);
      setShowPaymentModal(false);
      setPaymentAmount('');
      setCapitalPayment('');
      setInterestPayment('');
      setIvaInterest('');
      setPaymentDate('');
      setSelectedCreditor(null);
      loadCreditors();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'PF',
      credit_type: 'simple',
      rate: '',
      loan_amount: '',
      start_date: '',
      term_months: 12,
      monthly_payment: '',
      vehicle_value: '',
      initial_payment: '',
      total_rents: '',
      purchase_option: '',
      vehicle_id: ''
    });
  };

  const handleEdit = (creditor) => {
    setEditingCreditor(creditor);
    setFormData({
      name: creditor.name || '',
      type: creditor.type || 'PF',
      credit_type: creditor.credit_type || 'simple',
      rate: creditor.rate || '',
      loan_amount: creditor.loan_amount || '',
      start_date: creditor.start_date || '',
      term_months: creditor.term_months || 12,
      monthly_payment: creditor.monthly_payment || '',
      vehicle_value: creditor.vehicle_value || '',
      initial_payment: creditor.initial_payment || '',
      total_rents: creditor.total_rents || '',
      purchase_option: creditor.purchase_option || '',
      vehicle_id: creditor.vehicle_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (creditor) => {
    const confirmMsg = `¿Eliminar el acreedor "${creditor.name}"?\n\nEsto borrará el acreedor y todos sus pagos registrados. Esta acción no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await creditorsApi.delete(creditor.id);
      loadCreditors();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleViewHistory = async (creditor) => {
    setSelectedCreditor(creditor);
    setHistoryLoading(true);
    setShowHistoryModal(true);
    try {
      const { data } = await creditorsApi.getPayments(creditor.id);
      setCreditorPayments(data);
    } catch (err) {
      setCreditorPayments([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeleteCreditorPayment = async (payment) => {
    if (!selectedCreditor) return;
    const dateLabel = payment.payment_date
      ? new Date(payment.payment_date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
      : 'sin fecha';
    if (!window.confirm(
      `¿Eliminar el pago del ${dateLabel} por ${new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(payment.amount || 0)}?\n\n` +
      'Esta acción reintegrará el capital al saldo del acreedor y recalculará el "Saldo Después" ' +
      'de los pagos posteriores. No se puede deshacer.'
    )) return;
    try {
      await creditorsApi.deleteCreditorPayment(selectedCreditor.id, payment.id);
      // Refresh payment list + creditor list, then re-sync selectedCreditor
      const [{ data: refreshed }, listRes] = await Promise.all([
        creditorsApi.getPayments(selectedCreditor.id),
        creditorsApi.getAll(),
      ]);
      setCreditorPayments(refreshed);
      if (Array.isArray(listRes?.data)) {
        setCreditors(listRes.data);
        const upd = listRes.data.find((c) => c.id === selectedCreditor.id);
        if (upd) setSelectedCreditor(upd);
      }
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const openMonthlySummary = async () => {
    setShowMonthlySummary(true);
    setMonthlyLoading(true);
    try {
      const { data } = await creditorsApi.getMonthlySummary();
      setMonthlyData(data);
      if (data.length > 0) setSelectedMonth(data[0].month);
    } catch (err) {
      setMonthlyData([]);
      setError(formatApiError(err));
    } finally {
      setMonthlyLoading(false);
    }
  };

  const handleViewAmortization = async (creditor) => {
    setSelectedCreditor(creditor);
    setAmortLoading(true);
    setShowAmortModal(true);
    try {
      const { data } = await creditorsApi.getAmortization(creditor.id);
      setAmortData(data);
    } catch (err) {
      setAmortData(null);
      setError(formatApiError(err));
    } finally {
      setAmortLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const computeEndDate = (startDateStr, termMonths) => {
    if (!startDateStr || !termMonths) return '-';
    const [y, m, dd] = startDateStr.split('-').map(Number);
    if (!y || !m || !dd) return '-';
    const d = new Date(y, m - 1, dd);
    d.setMonth(d.getMonth() + Number(termMonths));
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const filteredCreditors = creditors.filter(
    (c) => c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalMonthlyPayment = creditors.reduce((sum, c) => sum + (c.monthly_payment || 0), 0);
  const totalLoan = creditors.reduce((sum, c) => sum + (c.loan_amount || 0), 0);
  const totalInitialPayments = creditors.filter(c => c.credit_type === 'arrendamiento').reduce((sum, c) => sum + (c.initial_payment || 0), 0);
  const totalRents = creditors.filter(c => c.credit_type === 'arrendamiento').reduce((sum, c) => sum + (c.total_rents || 0), 0);
  const totalRentasPorPagar = creditors.filter(c => c.credit_type === 'arrendamiento').reduce((sum, c) => sum + (c.rentas_pendientes || 0), 0);
  // Saldo Actual: solo créditos Simple + Revolvente
  const totalSaldoSimpleRevol = creditors
    .filter(c => c.credit_type === 'simple' || c.credit_type === 'revolvente')
    .reduce((sum, c) => sum + (c.current_balance || 0), 0);
  // Deuda Total: Rentas por pagar (arrendamientos) + Saldo Actual (simple + revolvente)
  const totalDebt = totalRentasPorPagar + totalSaldoSimpleRevol;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="creditors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Acreedores</h1>
          <p className="text-[#64748B]">{creditors.length} acreedores registrados</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openMonthlySummary}
            className="btn-secondary flex items-center gap-2"
            data-testid="open-monthly-summary-btn"
          >
            <CalendarBlank size={18} />
            Resumen mensual
          </button>
          {canManage && (
            <button
              onClick={handlePersistSnapshot}
              disabled={persistingSnapshot || creditors.length === 0}
              className="btn-secondary flex items-center gap-2"
              data-testid="persist-snapshot-btn"
              title="Graba en la BD los valores actualmente mostrados en la tabla como versión final"
            >
              <Check size={18} />
              {persistingSnapshot ? 'Guardando…' : 'Guardar versión final'}
            </button>
          )}
          {isAdmin && (
            <>
              <input
                ref={excelFileRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleBulkLinkExcel}
                className="hidden"
                data-testid="bulk-link-excel-input"
              />
              <button
                onClick={() => excelFileRef.current?.click()}
                disabled={linkingExcel}
                className="btn-secondary flex items-center gap-2"
                data-testid="bulk-link-excel-btn"
                title="Sube un Excel con columnas Acreedor, Pago Mensual y Placas para vincular masivamente los acreedores a sus vehículos"
              >
                <TableIcon size={18} />
                {linkingExcel ? 'Vinculando…' : 'Vincular por Excel'}
              </button>
            </>
          )}
          {isAdmin && (
            <button
              onClick={handleBackfillArrendamientos}
              disabled={backfilling}
              className="btn-secondary flex items-center gap-2"
              data-testid="backfill-arrendamiento-btn"
              title="Recalcula el desglose (Capital/Interés/IVA/Saldo Después) de todos los pagos mensuales de acreedores tipo Arrendamiento"
            >
              <TableIcon size={18} />
              {backfilling ? 'Recalculando…' : 'Recalcular Arrendamientos'}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={handleRegisterArrendamientosThrough}
              disabled={registering}
              className="btn-secondary flex items-center gap-2"
              data-testid="register-arrendamiento-through-btn"
              title="Registra como Pagados los pagos mensuales de arrendamiento faltantes hasta una fecha objetivo"
            >
              <CalendarBlank size={18} />
              {registering ? 'Registrando…' : 'Registrar pagos hasta…'}
            </button>
          )}
          {canManage && (
            <button
              onClick={() => {
                setEditingCreditor(null);
                resetForm();
                setShowModal(true);
              }}
              className="btn-primary flex items-center gap-2"
              data-testid="add-creditor-btn"
            >
              <Plus size={18} />
              Nuevo Acreedor
            </button>
          )}
          <a
            href={creditorsApi.exportExcelUrl(searchTerm ? { q: searchTerm } : {})}
            download
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            title={searchTerm ? `Descargar filtrado por "${searchTerm}" a Excel` : 'Descargar tabla de acreedores a Excel'}
            data-testid="export-creditors-excel"
          >
            <DownloadSimple size={16} weight="bold" /> Excel{searchTerm ? ' (filtro)' : ''}
          </a>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger flex items-center gap-2">
          <Warning size={20} />
          {error}
        </div>
      )}

      {snapshotMsg && (
        <div
          className="alert alert-success flex items-center justify-between gap-2"
          data-testid="snapshot-success-msg"
        >
          <div className="flex items-center gap-2">
            <Check size={20} />
            {snapshotMsg}
          </div>
          <button
            onClick={() => setSnapshotMsg('')}
            className="text-[#0F172A] hover:opacity-70"
            aria-label="cerrar"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="kpi-card">
          <p className="kpi-label">Pagos Iniciales</p>
          <p className="text-lg font-bold text-[#64748B]">{formatCurrency(totalInitialPayments)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Total Rentas</p>
          <p className="text-lg font-bold text-[#0055FF]">{formatCurrency(totalRents)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Rentas por Pagar</p>
          <p className="text-lg font-bold text-[#F59E0B]">{formatCurrency(totalRentasPorPagar)}</p>
        </div>
        <div className="kpi-card" data-testid="kpi-saldo-simple-revol">
          <p className="kpi-label">Saldo Actual</p>
          <p className="text-xs text-[#64748B] -mt-0.5">Simple + Revolvente</p>
          <p className="text-lg font-bold text-[#7C3AED]">{formatCurrency(totalSaldoSimpleRevol)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Deuda Total</p>
          <p className="text-lg font-bold text-[#E11D48]">{formatCurrency(totalDebt)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Pago Mensual Total</p>
          <p className="text-lg font-bold text-[#F59E0B]">{formatCurrency(totalMonthlyPayment)}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por nombre..."
          className="input pl-10"
          data-testid="search-creditors-input"
        />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }} data-testid="creditors-table">
            <thead>
              <tr>
                <th className="py-3 px-4 whitespace-nowrap">Nombre</th>
                <th className="py-3 px-4 whitespace-nowrap">Vehículo</th>
                <th className="py-3 px-4 whitespace-nowrap">Tipo</th>
                <th className="py-3 px-4 whitespace-nowrap">Tipo Crédito</th>
                <th className="py-3 px-4 whitespace-nowrap">Tasa</th>
                <th className="py-3 px-4 whitespace-nowrap">Monto Préstamo</th>
                <th className="py-3 px-4 whitespace-nowrap">Saldo Actual</th>
                <th className="py-3 px-4 whitespace-nowrap">Pago Mensual</th>
                <th className="py-3 px-4 whitespace-nowrap">Plazo</th>
                <th className="py-3 px-4 whitespace-nowrap">Inicio</th>
                <th className="py-3 px-4 whitespace-nowrap">Fin</th>
                <th className="py-3 px-4 whitespace-nowrap">Valor Vehículo</th>
                <th className="py-3 px-4 whitespace-nowrap">Pago Inicial</th>
                <th className="py-3 px-4 whitespace-nowrap">Total Rentas</th>
                <th className="py-3 px-4 whitespace-nowrap">Rentas por Pagar</th>
                <th className="py-3 px-4 whitespace-nowrap">Opción Compra</th>
                <th className="py-3 px-4 whitespace-nowrap">Estatus</th>
                {canEdit && <th className="py-3 px-4 whitespace-nowrap">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {filteredCreditors.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 18 : 17} className="text-center py-8 text-[#64748B]">
                    No se encontraron acreedores
                  </td>
                </tr>
              ) : (
                filteredCreditors.map((creditor) => {
                  const isLease = creditor.credit_type === 'arrendamiento';
                  const vLinked = vehicles.find((v) => v.id === creditor.vehicle_id);
                  return (
                  <tr key={creditor.id} data-testid={`creditor-row-${creditor.id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#F1F5F9] rounded-full flex items-center justify-center">
                          <Bank size={16} className="text-[#64748B]" />
                        </div>
                        <span className="font-medium">{creditor.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm whitespace-nowrap" data-testid={`creditor-vehicle-${creditor.id}`}>
                      {vLinked
                        ? (<><span className="font-medium">{vLinked.brand} {vLinked.sub_brand}</span> <span className="text-[#64748B]">· {vLinked.plates}</span></>)
                        : (creditor.vehicle_plate ? <span className="text-[#64748B]">{creditor.vehicle_plate}</span> : <span className="text-[#CBD5E1]">—</span>)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="badge badge-neutral">{creditor.type}</span>
                    </td>
                    <td className="py-3 px-4 capitalize">{creditor.credit_type}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Percent size={14} className="text-[#64748B]" />
                        {Number(creditor.rate || 0).toFixed(2)}%
                      </div>
                    </td>
                    <td className="py-3 px-4">{isLease ? '-' : formatCurrency(creditor.loan_amount)}</td>
                    <td className="py-3 px-4 font-bold text-[#E11D48]">{formatCurrency(creditor.current_balance)}</td>
                    <td className="py-3 px-4">{formatCurrency(creditor.monthly_payment)}</td>
                    <td className="py-3 px-4">{creditor.term_months} meses</td>
                    <td className="py-3 px-4 whitespace-nowrap" data-testid={`creditor-start-${creditor.id}`}>
                      {formatShortDate(creditor.start_date)}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap" data-testid={`creditor-end-${creditor.id}`}>
                      {computeEndDate(creditor.start_date, creditor.term_months)}
                    </td>
                    <td className="py-3 px-4">{isLease ? formatCurrency(creditor.vehicle_value) : '-'}</td>
                    <td className="py-3 px-4">{isLease ? formatCurrency(creditor.initial_payment) : '-'}</td>
                    <td className="py-3 px-4">{isLease ? formatCurrency(creditor.total_rents) : '-'}</td>
                    <td className={`py-3 px-4 ${isLease ? 'font-medium text-[#F59E0B]' : ''}`}>{isLease ? formatCurrency(creditor.rentas_pendientes) : '-'}</td>
                    <td className="py-3 px-4">{isLease ? formatCurrency(creditor.purchase_option) : '-'}</td>
                    <td className="py-3 px-4">
                      <select
                        value={creditor.status || 'activo'}
                        onChange={(e) => handleStatusChange(creditor.id, e.target.value)}
                        className={`text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer ${statusColors[creditor.status] || 'badge-neutral'}`}
                        data-testid={`status-creditor-${creditor.id}`}
                      >
                        {Object.entries(statusLabels).map(([val, label]) => (
                          <option key={val} value={val}>{label}</option>
                        ))}
                      </select>
                    </td>
                    {canEdit && (
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewHistory(creditor)}
                            className="p-2 text-[#64748B] hover:text-[#0055FF] hover:bg-blue-50 rounded"
                            title="Ver historial de pagos"
                            data-testid={`history-creditor-${creditor.id}`}
                          >
                            <Eye size={18} />
                          </button>
                          {(creditor.credit_type === 'simple' || creditor.credit_type === 'arrendamiento') && (
                            <button
                              onClick={() => handleViewAmortization(creditor)}
                              className="p-2 text-[#64748B] hover:text-[#8B5CF6] hover:bg-purple-50 rounded"
                              title="Tabla de Amortización"
                              data-testid={`amort-creditor-${creditor.id}`}
                            >
                              <TableIcon size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedCreditor(creditor);
                              setPaymentAmount(creditor.monthly_payment.toString());
                              setCapitalPayment('');
                              setPaymentDate(new Date().toISOString().slice(0, 10));
                              setShowPaymentModal(true);
                            }}
                            className="p-2 text-[#64748B] hover:text-[#10B981] hover:bg-green-50 rounded"
                            title="Registrar pago"
                            data-testid={`payment-creditor-${creditor.id}`}
                          >
                            <Money size={18} />
                          </button>
                          {canManage && (
                            <button
                              onClick={() => handleEdit(creditor)}
                              className="p-2 text-[#64748B] hover:text-[#0055FF] hover:bg-blue-50 rounded"
                              data-testid={`edit-creditor-${creditor.id}`}
                            >
                              <PencilSimple size={18} />
                            </button>
                          )}
                          {canManage && (
                            <button
                              onClick={() => handleDelete(creditor)}
                              className="p-2 text-[#64748B] hover:text-[#E11D48] hover:bg-red-50 rounded"
                              title="Eliminar acreedor"
                              data-testid={`delete-creditor-${creditor.id}`}
                            >
                              <Trash size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );})
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="creditor-modal">
          <div className="modal-content animate-fade-in max-w-2xl">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">
                  {editingCreditor ? 'Editar Acreedor' : 'Nuevo Acreedor'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="alert alert-danger text-sm">{formError}</div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Nombre *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    required
                    placeholder="Nombre del acreedor"
                    data-testid="creditor-name-input"
                  />
                </div>
                <div>
                  <label className="label">Tipo</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="input"
                    data-testid="creditor-type-select"
                  >
                    <option value="PF">Persona Física</option>
                    <option value="PM">Persona Moral</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Tipo de Crédito</label>
                  <select
                    value={formData.credit_type}
                    onChange={(e) => setFormData({ ...formData, credit_type: e.target.value })}
                    className="input"
                    data-testid="creditor-credit-type-select"
                  >
                    <option value="simple">Simple</option>
                    <option value="revolvente">Revolvente</option>
                    <option value="arrendamiento">Arrendamiento</option>
                  </select>
                </div>
                <div>
                  <label className="label">Vehículo asociado</label>
                  <select
                    value={formData.vehicle_id}
                    onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                    className="input"
                    data-testid="creditor-vehicle-select"
                  >
                    <option value="">— Sin vehículo —</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.brand} {v.sub_brand} · {v.plates} ({v.year})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Tasa Anual (%) *</label>
                  <input
                    type="number"
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    className="input"
                    required
                    min="0"
                    step="0.01"
                    placeholder="18"
                    data-testid="creditor-rate-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Monto del Préstamo *</label>
                  <input
                    type="number"
                    value={formData.loan_amount}
                    onChange={(e) => setFormData({ ...formData, loan_amount: e.target.value })}
                    className="input"
                    required
                    min="0"
                    step="0.01"
                    placeholder="500000"
                    data-testid="creditor-loan-input"
                  />
                </div>
                <div>
                  <label className="label">Fecha de Inicio *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="input"
                    required
                    data-testid="creditor-date-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Plazo (meses) *</label>
                  <input
                    type="number"
                    value={formData.term_months}
                    onChange={(e) => setFormData({ ...formData, term_months: e.target.value })}
                    className="input"
                    required
                    min="1"
                    data-testid="creditor-term-input"
                  />
                </div>
                <div>
                  <label className="label">Pago Mensual *</label>
                  <input
                    type="number"
                    value={formData.monthly_payment}
                    onChange={(e) => setFormData({ ...formData, monthly_payment: e.target.value })}
                    className="input"
                    required
                    min="0"
                    step="0.01"
                    placeholder="15000"
                    data-testid="creditor-payment-input"
                  />
                </div>
              </div>

              {formData.credit_type === 'arrendamiento' && (
                <>
                  <div className="border-t border-[#E2E8F0] pt-4 mt-2">
                    <p className="text-sm font-semibold text-[#0F172A] mb-3">Datos de Arrendamiento Financiero</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Valor del Vehículo *</label>
                      <input
                        type="number"
                        value={formData.vehicle_value}
                        onChange={(e) => setFormData({ ...formData, vehicle_value: e.target.value })}
                        className="input"
                        required
                        min="0"
                        step="0.01"
                        placeholder="424547"
                        data-testid="creditor-vehicle-value-input"
                      />
                    </div>
                    <div>
                      <label className="label">Pago Inicial *</label>
                      <input
                        type="number"
                        value={formData.initial_payment}
                        onChange={(e) => setFormData({ ...formData, initial_payment: e.target.value })}
                        className="input"
                        required
                        min="0"
                        step="0.01"
                        placeholder="21227.35"
                        data-testid="creditor-initial-payment-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Monto Total Rentas *</label>
                      <input
                        type="number"
                        value={formData.total_rents}
                        onChange={(e) => setFormData({ ...formData, total_rents: e.target.value })}
                        className="input"
                        required
                        min="0"
                        step="0.01"
                        placeholder="439896.96"
                        data-testid="creditor-total-rents-input"
                      />
                    </div>
                    <div>
                      <label className="label">Opción de Compra *</label>
                      <input
                        type="number"
                        value={formData.purchase_option}
                        onChange={(e) => setFormData({ ...formData, purchase_option: e.target.value })}
                        className="input"
                        required
                        min="0"
                        step="0.01"
                        placeholder="169818"
                        data-testid="creditor-purchase-option-input"
                      />
                    </div>
                  </div>
                  <div className="p-3 bg-[#F0FDF4] rounded-lg text-xs text-[#10B981]">
                    Saldo Actual y Rentas por Pagar se calculan automáticamente.
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-creditor-btn"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={18} />
                  )}
                  {editingCreditor ? 'Guardar Cambios' : 'Crear Acreedor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedCreditor && (
        <div className="modal-overlay" data-testid="creditor-payment-modal">
          <div className="modal-content animate-fade-in">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">Registrar Pago a Acreedor</h2>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedCreditor(null);
                  }}
                  className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handlePayment} className="p-6 space-y-4">
              {formError && (
                <div className="alert alert-danger text-sm">{formError}</div>
              )}

              <div className="p-4 bg-[#F8FAFC] rounded-lg space-y-2">
                <p className="text-sm text-[#64748B]">
                  Acreedor: <span className="font-medium text-[#0F172A]">{selectedCreditor.name}</span>
                </p>
                <p className="text-sm text-[#64748B]">
                  Tipo Crédito: <span className="font-medium text-[#0F172A] capitalize">{selectedCreditor.credit_type}</span>
                </p>
                <p className="text-sm text-[#64748B]">
                  Saldo actual: <span className="font-medium text-[#E11D48]">{formatCurrency(selectedCreditor.current_balance)}</span>
                </p>
                <p className="text-sm text-[#64748B]">
                  Pago mensual sugerido: <span className="font-medium text-[#0F172A]">{formatCurrency(selectedCreditor.monthly_payment)}</span>
                </p>
                {(selectedCreditor.credit_type === 'revolvente' || selectedCreditor.credit_type === 'simple') && (
                  <p className="text-xs text-[#F59E0B] bg-[#FFFBEB] px-2 py-1 rounded mt-2">
                    {selectedCreditor.credit_type === 'revolvente'
                      ? 'Crédito Revolvente: El pago mensual cubre solo intereses. Para reducir el saldo, registra un Abono a Capital.'
                      : 'Crédito Simple: Puedes registrar un Abono a Capital adicional al pago mensual.'}
                  </p>
                )}
              </div>

              <div>
                <label className="label">Fecha de Pago *</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="input"
                  required
                  data-testid="creditor-payment-date-input"
                />
              </div>

              {selectedCreditor.credit_type === 'arrendamiento' ? (
                <>
                  <div>
                    <label className="label">Pago Mensual de Capital</label>
                    <input
                      type="number"
                      value={capitalPayment}
                      onChange={(e) => {
                        setCapitalPayment(e.target.value);
                        const cap = parseFloat(e.target.value || 0);
                        const intr = parseFloat(interestPayment || 0);
                        const total = cap + intr;
                        setIvaInterest(total > 0 ? (total * 0.16).toFixed(2) : '');
                      }}
                      className="input"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      data-testid="creditor-lease-capital-input"
                    />
                  </div>
                  <div>
                    <label className="label">Pago Mensual de Intereses</label>
                    <input
                      type="number"
                      value={interestPayment}
                      onChange={(e) => {
                        setInterestPayment(e.target.value);
                        // IVA sobre la RENTA completa (capital + intereses)
                        const intr = parseFloat(e.target.value || 0);
                        const cap = parseFloat(capitalPayment || 0);
                        const total = cap + intr;
                        setIvaInterest(total > 0 ? (total * 0.16).toFixed(2) : '');
                      }}
                      className="input"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      data-testid="creditor-lease-interest-input"
                    />
                  </div>
                  <div>
                    <label className="label">IVA sobre Renta (16%)</label>
                    <input
                      type="number"
                      value={ivaInterest}
                      onChange={(e) => setIvaInterest(e.target.value)}
                      className="input"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      data-testid="creditor-lease-iva-input"
                    />
                    <p className="text-xs text-[#94A3B8] mt-1">
                      Auto-calculado como 16% de (Capital + Intereses). Puedes ajustarlo.
                    </p>
                  </div>
                  <div className="p-3 bg-[#F8FAFC] rounded-lg text-sm">
                    <p className="text-[#64748B]">
                      Total del pago:{' '}
                      <span className="font-bold text-[#0F172A]">
                        {formatCurrency(
                          (parseFloat(capitalPayment || 0)) +
                          (parseFloat(interestPayment || 0)) +
                          (parseFloat(ivaInterest || 0))
                        )}
                      </span>
                    </p>
                    <p className="text-xs text-[#94A3B8] mt-1">
                      Solo el Capital reduce el saldo del crédito. Intereses e IVA son costo financiero.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="label">Pago Mensual (Intereses) *</label>
                    <input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="input"
                      required
                      min="0"
                      step="0.01"
                      data-testid="creditor-payment-amount-input"
                    />
                  </div>

                  <div>
                    <label className="label">Abono a Capital (opcional)</label>
                    <input
                      type="number"
                      value={capitalPayment}
                      onChange={(e) => setCapitalPayment(e.target.value)}
                      className="input"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      data-testid="creditor-capital-payment-input"
                    />
                    {capitalPayment && parseFloat(capitalPayment) > 0 && (
                      <p className="text-xs text-[#10B981] mt-1">
                        Nuevo saldo después del abono: {formatCurrency(Math.max(0, selectedCreditor.current_balance - parseFloat(capitalPayment)))}
                      </p>
                    )}
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="confirm-creditor-payment-btn"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={18} />
                  )}
                  Registrar Pago
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showHistoryModal && selectedCreditor && (
        <div className="modal-overlay" data-testid="creditor-history-modal">
          <div className="modal-content animate-fade-in" style={{ maxWidth: '1100px', width: '95vw' }}>
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-heading font-bold">Historial de Pagos</h2>
                  <p className="text-sm text-[#64748B]">{selectedCreditor.name}</p>
                </div>
                <button
                  onClick={() => { setShowHistoryModal(false); setSelectedCreditor(null); }}
                  className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Creditor Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Monto Crédito</p>
                  <p className="text-base font-bold text-[#0F172A]">{formatCurrency(selectedCreditor.loan_amount)}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Saldo Actual</p>
                  <p className="text-base font-bold text-[#E11D48]">{formatCurrency(selectedCreditor.current_balance)}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Total Capital Pagado</p>
                  <p className="text-base font-bold text-[#7C3AED]" data-testid="total-capital-paid">
                    {formatCurrency(creditorPayments.reduce((s, p) => s + (p.capital_payment || 0), 0))}
                  </p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Total Pagado</p>
                  <p className="text-base font-bold text-emerald-600">
                    {formatCurrency(creditorPayments.reduce((s, p) => s + (p.amount || 0), 0))}
                  </p>
                </div>
              </div>

              {historyLoading ? (
                <div className="flex items-center justify-center py-8"><div className="spinner" /></div>
              ) : creditorPayments.length === 0 ? (
                <p className="text-center py-8 text-[#64748B]">No hay pagos registrados para este acreedor</p>
              ) : (
                <div className="max-h-96 overflow-auto">
                  <table className="data-table min-w-[1000px]" data-testid="creditor-history-table">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th>Fecha</th>
                        <th>Monto</th>
                        <th className="text-right">Capital</th>
                        <th className="text-right">Interés</th>
                        <th className="text-right">IVA Renta</th>
                        <th className="text-right">Saldo después</th>
                        <th>Tipo</th>
                        <th>Estatus</th>
                        {isAdmin && <th className="text-right">Acciones</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {creditorPayments.map((p, idx) => (
                        <tr key={p.id || p.excel_id || `payment-${p.payment_date}-${idx}`}>
                          <td className="text-sm">
                            <div className="flex items-center gap-1 whitespace-nowrap">
                              <CalendarBlank size={14} className="text-[#64748B]" />
                              {p.payment_date ? new Date(p.payment_date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                            </div>
                          </td>
                          <td className="text-sm font-semibold text-emerald-600 whitespace-nowrap">{formatCurrency(p.amount)}</td>
                          <td className="text-sm text-right whitespace-nowrap font-medium text-[#7C3AED]">
                            {formatCurrency(p.capital_payment || 0)}
                          </td>
                          <td className="text-sm text-right whitespace-nowrap text-[#64748B]">
                            {formatCurrency(p.interest_payment || 0)}
                          </td>
                          <td className="text-sm text-right whitespace-nowrap text-[#0055FF]">
                            {formatCurrency(p.iva_interest || 0)}
                          </td>
                          <td className="text-sm text-right whitespace-nowrap font-medium">
                            {(() => {
                              const saldo = p.saldo_despues ?? p.balance_after;
                              return (saldo !== undefined && saldo !== null) ? formatCurrency(saldo) : '-';
                            })()}
                          </td>
                          <td className="text-sm text-[#64748B]">{p.payment_type || '-'}</td>
                          <td>
                            <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                              {p.status || 'Pagado'}
                            </span>
                          </td>
                          {isAdmin && (
                            <td className="text-right">
                              {p.id ? (
                                <button
                                  onClick={() => handleDeleteCreditorPayment(p)}
                                  className="p-1.5 text-[#E11D48] hover:bg-red-50 rounded transition-colors"
                                  title="Eliminar pago"
                                  data-testid={`delete-creditor-payment-${p.id}`}
                                >
                                  <Trash size={16} />
                                </button>
                              ) : (
                                <span className="text-xs text-[#94A3B8]" title="Pago legacy sin ID">-</span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Amortization Modal */}
      {showAmortModal && selectedCreditor && (
        <div className="modal-overlay" data-testid="amortization-modal">
          <div className="modal-content animate-fade-in max-w-5xl">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-heading font-bold">Tabla de Amortización</h2>
                  <p className="text-sm text-[#64748B]">{selectedCreditor.name} - {selectedCreditor.credit_type?.toUpperCase()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={creditorsApi.exportAmortizationExcel(selectedCreditor.id)}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[#10B981] bg-[#F0FDF4] rounded-lg hover:bg-[#DCFCE7] transition-colors"
                    data-testid="export-amort-excel"
                  >
                    <FileXls size={16} /> Excel
                  </a>
                  <a
                    href={creditorsApi.exportAmortizationPdf(selectedCreditor.id)}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[#E11D48] bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    data-testid="export-amort-pdf"
                  >
                    <FilePdf size={16} /> PDF
                  </a>
                  <button onClick={() => setShowAmortModal(false)} className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"><X size={20} /></button>
                </div>
              </div>
            </div>

            {amortLoading ? (
              <div className="p-12 flex items-center justify-center"><div className="spinner" /></div>
            ) : amortData ? (
              <div className="p-6">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  <div className="p-3 bg-[#F8FAFC] rounded-lg">
                    <p className="text-[10px] text-[#64748B] uppercase font-medium">Tasa Implícita</p>
                    <p className="text-sm font-bold text-[#0F172A]">{Number(amortData.rate || 0).toFixed(2)}% anual</p>
                  </div>
                  <div className="p-3 bg-[#F8FAFC] rounded-lg">
                    <p className="text-[10px] text-[#64748B] uppercase font-medium">Fecha Inicio</p>
                    <p className="text-sm font-bold text-[#0F172A]">{amortData.start_date}</p>
                  </div>
                  <div className="p-3 bg-[#F8FAFC] rounded-lg">
                    <p className="text-[10px] text-[#64748B] uppercase font-medium">Fecha Final</p>
                    <p className="text-sm font-bold text-[#0F172A]">{amortData.end_date}</p>
                  </div>
                  <div className="p-3 bg-[#F8FAFC] rounded-lg">
                    <p className="text-[10px] text-[#64748B] uppercase font-medium">Plazo</p>
                    <p className="text-sm font-bold text-[#0F172A]">{amortData.term_months} meses</p>
                  </div>
                </div>

                {/* Table */}
                <div className="max-h-[55vh] overflow-y-auto">
                  <table className="data-table text-xs">
                    <thead>
                      <tr>
                        <th>Mes</th>
                        <th>Fecha</th>
                        <th>Saldo Inicial</th>
                        {amortData.credit_type === 'arrendamiento' ? (
                          <>
                            <th>Renta s/IVA</th>
                            <th>Interés</th>
                            <th>Capital</th>
                            <th>IVA</th>
                            <th>Renta c/IVA</th>
                          </>
                        ) : (
                          <>
                            <th>Pago</th>
                            <th>Interés</th>
                            <th>Capital</th>
                          </>
                        )}
                        <th>Saldo Final</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amortData.table.map((row) => (
                        <tr key={row.month}>
                          <td className="font-medium">{row.month}</td>
                          <td>{row.payment_date}</td>
                          <td className="text-right">{formatCurrency(row.initial_balance)}</td>
                          {amortData.credit_type === 'arrendamiento' ? (
                            <>
                              <td className="text-right">{formatCurrency(row.rent_no_iva)}</td>
                              <td className="text-right text-[#F59E0B]">{formatCurrency(row.interest)}</td>
                              <td className="text-right text-[#10B981]">{formatCurrency(row.capital)}</td>
                              <td className="text-right text-[#64748B]">{formatCurrency(row.iva)}</td>
                              <td className="text-right font-medium">{formatCurrency(row.rent_with_iva)}</td>
                            </>
                          ) : (
                            <>
                              <td className="text-right font-medium">{formatCurrency(row.payment)}</td>
                              <td className="text-right text-[#F59E0B]">{formatCurrency(row.interest)}</td>
                              <td className="text-right text-[#10B981]">{formatCurrency(row.capital)}</td>
                            </>
                          )}
                          <td className="text-right font-bold">{formatCurrency(row.final_balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#F1F5F9] font-bold">
                        <td colSpan={2}>TOTALES</td>
                        <td></td>
                        {amortData.credit_type === 'arrendamiento' ? (
                          <>
                            <td className="text-right">{formatCurrency(amortData.table.reduce((s, r) => s + r.rent_no_iva, 0))}</td>
                            <td className="text-right text-[#F59E0B]">{formatCurrency(amortData.table.reduce((s, r) => s + r.interest, 0))}</td>
                            <td className="text-right text-[#10B981]">{formatCurrency(amortData.table.reduce((s, r) => s + r.capital, 0))}</td>
                            <td className="text-right">{formatCurrency(amortData.table.reduce((s, r) => s + r.iva, 0))}</td>
                            <td className="text-right">{formatCurrency(amortData.table.reduce((s, r) => s + r.rent_with_iva, 0))}</td>
                          </>
                        ) : (
                          <>
                            <td className="text-right">{formatCurrency(amortData.table.reduce((s, r) => s + r.payment, 0))}</td>
                            <td className="text-right text-[#F59E0B]">{formatCurrency(amortData.table.reduce((s, r) => s + r.interest, 0))}</td>
                            <td className="text-right text-[#10B981]">{formatCurrency(amortData.table.reduce((s, r) => s + r.capital, 0))}</td>
                          </>
                        )}
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : (
              <div className="p-12 text-center text-[#64748B]">No se pudo cargar la tabla</div>
            )}
          </div>
        </div>
      )}

      {/* Monthly Summary Modal */}
      {showMonthlySummary && (
        <div className="modal-overlay" data-testid="monthly-summary-modal">
          <div className="modal-content animate-fade-in" style={{ maxWidth: '1200px', width: '95vw' }}>
            <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between">
              <div>
                <h2 className="text-xl font-heading font-bold">Pagos a Acreedores — Resumen Mensual</h2>
                <p className="text-sm text-[#64748B]">
                  Total pagado por mes y acreedor
                </p>
              </div>
              <button
                onClick={() => setShowMonthlySummary(false)}
                className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {monthlyLoading ? (
                <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
              ) : monthlyData.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">No hay pagos registrados a acreedores.</div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-[#0F172A]">Mes:</label>
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="input-field text-sm min-w-[180px]"
                      data-testid="monthly-summary-month-select"
                    >
                      {monthlyData.map((m) => {
                        const [y, mo] = m.month.split('-');
                        const MONTHS = {
                          '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril',
                          '05': 'Mayo', '06': 'Junio', '07': 'Julio', '08': 'Agosto',
                          '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
                        };
                        return (
                          <option key={m.month} value={m.month}>
                            {MONTHS[mo]} {y} — {formatCurrency(m.total)}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {(() => {
                    const month = monthlyData.find((m) => m.month === selectedMonth);
                    if (!month) return null;
                    return (
                      <div className="card overflow-hidden">
                        <div className="bg-[#F8FAFC] px-4 py-3 border-b border-[#E2E8F0] flex justify-between items-center">
                          <span className="font-medium text-[#0F172A]">{month.items.length} acreedor(es)</span>
                          <span className="font-bold text-[#10B981]">{formatCurrency(month.total)}</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="data-table min-w-[700px]">
                            <thead>
                              <tr>
                                <th>Acreedor</th>
                                <th>Tipo</th>
                                <th>Pagos</th>
                                <th className="text-right">Capital</th>
                                <th className="text-right">Interés</th>
                                <th className="text-right">Total pagado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {month.items.map((row) => (
                                <tr key={row.creditor_id} data-testid={`monthly-row-${row.creditor_id}`}>
                                  <td className="font-medium whitespace-nowrap">{row.creditor_name}</td>
                                  <td>
                                    <span className="badge badge-neutral capitalize">{row.credit_type || '-'}</span>
                                  </td>
                                  <td>{row.count}</td>
                                  <td className="text-right whitespace-nowrap">{formatCurrency(row.capital)}</td>
                                  <td className="text-right whitespace-nowrap">{formatCurrency(row.interest)}</td>
                                  <td className="text-right font-bold text-[#10B981] whitespace-nowrap">{formatCurrency(row.total)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Overall summary by creditor across all months */}
                  <div className="card overflow-hidden">
                    <div className="bg-[#F8FAFC] px-4 py-3 border-b border-[#E2E8F0]">
                      <span className="font-medium text-[#0F172A]">Histórico — Total por mes</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="data-table min-w-[500px]">
                      <thead>
                        <tr>
                          <th>Mes</th>
                          <th>Acreedores con pago</th>
                          <th className="text-right">Total pagado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyData.map((m) => {
                          const [y, mo] = m.month.split('-');
                          const MONTHS = {
                            '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril',
                            '05': 'Mayo', '06': 'Junio', '07': 'Julio', '08': 'Agosto',
                            '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
                          };
                          return (
                            <tr
                              key={m.month}
                              className={m.month === selectedMonth ? 'bg-blue-50 cursor-pointer' : 'cursor-pointer hover:bg-[#F8FAFC]'}
                              onClick={() => setSelectedMonth(m.month)}
                              data-testid={`monthly-history-row-${m.month}`}
                            >
                              <td className="font-medium">{MONTHS[mo]} {y}</td>
                              <td>{m.items.length}</td>
                              <td className="text-right font-bold">{formatCurrency(m.total)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Creditors;